/**
 * Prose spell / sentence styling for the syntax highlight layer (Normal text + Max).
 * Word list derived from google-10000-english (CC0); see `data/google-3500-english-words.txt`.
 */
import type { LanguageMode } from "../doc/languageMode.js";
import { escapeHtml } from "./escapeHtml.js";
import commonWordsRaw from "./data/google-3500-english-words.txt?raw";

const commonEnglishWordSet: ReadonlySet<string> = new Set(
  commonWordsRaw
    .split(/\r?\n/)
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean),
);
let exceptionWordSet: ReadonlySet<string> = new Set();

const WORD_RE = /[A-Za-z]+(?:'[A-Za-z]+)?/g;

export function setSpellcheckExceptionWords(words: readonly string[]): void {
  exceptionWordSet = new Set(
    words
      .map((w) => w.trim().toLowerCase())
      .filter((w) => w.length >= 2 && /^[a-z']+$/.test(w)),
  );
}

export function normalizeSpellcheckExceptionWord(word: string): string {
  return word.trim().toLowerCase();
}

export function languageModeUsesProseSpellHighlight(mode: LanguageMode): boolean {
  return mode === "text" || mode === "max";
}

function splitMaxDecoratableSegments(line: string): { readonly skip: boolean; readonly text: string }[] {
  const out: { skip: boolean; text: string }[] = [];
  let i = 0;
  while (i < line.length) {
    if (line.startsWith("$$", i)) {
      const j = line.indexOf("$$", i + 2);
      if (j === -1) {
        out.push({ skip: true, text: line.slice(i) });
        break;
      }
      out.push({ skip: true, text: line.slice(i, j + 2) });
      i = j + 2;
      continue;
    }
    if (line.startsWith("@@", i)) {
      const j = line.indexOf("@@", i + 2);
      if (j === -1) {
        out.push({ skip: true, text: line.slice(i) });
        break;
      }
      out.push({ skip: true, text: line.slice(i, j + 2) });
      i = j + 2;
      continue;
    }
    let j = i;
    while (j < line.length && !line.startsWith("$$", j) && !line.startsWith("@@", j)) {
      j++;
    }
    if (j > i) out.push({ skip: false, text: line.slice(i, j) });
    i = j;
  }
  return out;
}

/** Character ranges [start, end) for simple sentence chunks (split at . ! ? … followed by spaces). */
function sentenceRanges(text: string): [number, number][] {
  if (text.length === 0) return [];
  const ranges: [number, number][] = [];
  let start = 0;
  for (let p = 0; p < text.length; p++) {
    const c = text[p]!;
    if (/[.!?…]/.test(c)) {
      let end = p + 1;
      while (end < text.length && /\s/.test(text[end]!)) end++;
      ranges.push([start, end]);
      start = end;
      p = end - 1;
    }
  }
  if (start < text.length) ranges.push([start, text.length]);
  return ranges;
}

function dictHasWord(lower: string): boolean {
  if (exceptionWordSet.has(lower)) return true;
  if (commonEnglishWordSet.has(lower)) return true;
  if (lower.endsWith("'s")) {
    const stem = lower.slice(0, -2);
    if (stem.length >= 1 && commonEnglishWordSet.has(stem)) return true;
  }
  return false;
}

function shouldMarkMisspelled(word: string): boolean {
  if (word.length < 2) return false;
  if (/\d/.test(word)) return false;
  if (/[^A-Za-z']/.test(word)) return false;
  const lower = word.toLowerCase();
  if (dictHasWord(lower)) return false;
  /** Short all-caps tokens (API, ID) — heuristic skip. */
  if (word.length <= 5 && word === word.toUpperCase() && /^[A-Z]+$/.test(word)) return false;
  return true;
}

export function isMisspelledWord(word: string): boolean {
  return shouldMarkMisspelled(word);
}

function editDistanceLimited(a: string, b: string, maxDistance: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > maxDistance) return maxDistance + 1;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let minRow = curr[0]!;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j]! + 1,
        curr[j - 1]! + 1,
        prev[j - 1]! + cost,
      );
      if (curr[j]! < minRow) minRow = curr[j]!;
    }
    if (minRow > maxDistance) return maxDistance + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j]!;
  }
  return prev[b.length]!;
}

export function suggestSpellings(word: string, limit = 6): readonly string[] {
  const normalized = normalizeSpellcheckExceptionWord(word);
  if (normalized.length < 2 || !/^[a-z']+$/.test(normalized)) return [];
  const first = normalized[0]!;
  const maxDistance = normalized.length <= 4 ? 1 : 2;
  const candidates: { word: string; score: number }[] = [];
  for (const candidate of commonEnglishWordSet) {
    if (candidate[0] !== first) continue;
    const d = editDistanceLimited(normalized, candidate, maxDistance);
    if (d > maxDistance) continue;
    const score = d * 10 + Math.abs(candidate.length - normalized.length);
    candidates.push({ word: candidate, score });
  }
  candidates.sort((a, b) => a.score - b.score || a.word.localeCompare(b.word));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    if (seen.has(c.word)) continue;
    seen.add(c.word);
    out.push(c.word);
    if (out.length >= limit) break;
  }
  return out;
}

function decorateWordsInSentence(sentence: string): string {
  let last = 0;
  let out = "";
  let m: RegExpExecArray | null;
  while ((m = WORD_RE.exec(sentence)) !== null) {
    const word = m[0]!;
    const idx = m.index;
    out += escapeHtml(sentence.slice(last, idx));
    if (shouldMarkMisspelled(word)) {
      out += `<span class="npp-prose-misspell">${escapeHtml(word)}</span>`;
    } else {
      out += escapeHtml(word);
    }
    last = idx + word.length;
  }
  WORD_RE.lastIndex = 0;
  out += escapeHtml(sentence.slice(last));
  return out;
}

function sentenceHasMisspelling(sentence: string): boolean {
  let m: RegExpExecArray | null;
  while ((m = WORD_RE.exec(sentence)) !== null) {
    if (shouldMarkMisspelled(m[0]!)) {
      WORD_RE.lastIndex = 0;
      return true;
    }
  }
  WORD_RE.lastIndex = 0;
  return false;
}

export function wordRangeAt(text: string, offset: number): { start: number; end: number; word: string } | null {
  if (!text) return null;
  const at = Math.max(0, Math.min(offset, text.length));
  let m: RegExpExecArray | null;
  while ((m = WORD_RE.exec(text)) !== null) {
    const start = m.index;
    const word = m[0]!;
    const end = start + word.length;
    if (at >= start && at <= end) {
      WORD_RE.lastIndex = 0;
      return { start, end, word };
    }
  }
  WORD_RE.lastIndex = 0;
  return null;
}

function decorateProseSegment(segment: string): string {
  const parts: string[] = [];
  for (const [a, b] of sentenceRanges(segment)) {
    const chunk = segment.slice(a, b);
    if (chunk.length === 0) continue;
    const inner = decorateWordsInSentence(chunk);
    if (sentenceHasMisspelling(chunk)) {
      parts.push(`<span class="npp-prose-sentence npp-prose-sentence--has-misspell">${inner}</span>`);
    } else {
      parts.push(inner);
    }
  }
  return parts.join("");
}

function markdownHljsTextNodeSkippedForSpell(textNode: Text): boolean {
  let el: HTMLElement | null = textNode.parentElement;
  while (el) {
    const t = el.tagName;
    if (t === "CODE" || t === "PRE") return true;
    if (el.classList.contains("hljs-code")) return true;
    el = el.parentElement;
  }
  return false;
}

/**
 * After highlight.js Markdown HTML is built, decorate prose `Text` nodes with the same
 * misspelling / sentence marks as Normal text, skipping fenced and inline code regions.
 */
function collectMarkdownHljsTextNodes(root: Node, acc: Text[]): void {
  for (const child of root.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      const tn = child as Text;
      if (tn.data && /[^\s]/.test(tn.data) && !markdownHljsTextNodeSkippedForSpell(tn)) {
        acc.push(tn);
      }
    } else {
      collectMarkdownHljsTextNodes(child, acc);
    }
  }
}

export function injectProseSpellHighlightIntoMarkdownHljs(hlHtml: string): string {
  if (typeof document === "undefined") return hlHtml;
  const wrap = document.createElement("div");
  wrap.innerHTML = hlHtml;
  const textNodes: Text[] = [];
  collectMarkdownHljsTextNodes(wrap, textNodes);
  for (const textNode of textNodes) {
    const decorated = decorateProseSegment(textNode.data);
    if (decorated === textNode.data) continue;
    const frag = document.createRange().createContextualFragment(decorated);
    textNode.parentNode?.replaceChild(frag, textNode);
  }
  return wrap.innerHTML;
}

/** One logical line (no `\n`); returns safe HTML fragment (no outer wrapper). */
export function renderPlaintextLineWithProseSpellHighlight(line: string, mode: LanguageMode): string {
  if (mode !== "max") return decorateProseSegment(line);
  let out = "";
  for (const seg of splitMaxDecoratableSegments(line)) {
    out += seg.skip ? escapeHtml(seg.text) : decorateProseSegment(seg.text);
  }
  return out;
}
